import React, { useState, useCallback, ReactElement } from 'react';
import * as css from './BuildData.module.scss';
import 'src/css/themes.scss';
import { IconSeeMore, IconSortAscending, IconSortDescending, IconPrint } from 'src/content/icons';
import { ConnectedVehicleRec, OPTION } from 'src/models';
import { Dialog, ScrollBar } from '@framework-controls/core';
import { strings } from 'src/localization';
import { random } from 'lodash';

export interface IProps {
    connectedVehicle: ConnectedVehicleRec;
    printConnectedVehicle: (filter: string) => void;
}

const BuildData = (props: IProps) => {
    const [buildOpen, setBuildOpen] = useState(false);
    const [sortReversed, setSortReversed] = useState(false);
    const [sortIconColumn, setSortIconColumn] = useState('none');
    const toggleBuild = useCallback(() => {
        setBuildOpen(!buildOpen);
    }, [buildOpen]);
    const reverseSort = useCallback(() => {
        setSortReversed(!sortReversed);
    }, [sortReversed]);
    const printCV = useCallback(() => {
        props.printConnectedVehicle('B');
    }, [props.printConnectedVehicle]);
    const moveFocusToDesc = useCallback(() => {
        if (sortIconColumn !== 'desc') {
            setSortReversed(false);
        }
        setSortIconColumn('desc');
    }, [sortIconColumn]);
    const moveFocusToCode = useCallback(() => {
        if (sortIconColumn !== 'code') {
            setSortReversed(false);
        }
        setSortIconColumn('code');
    }, [sortIconColumn]);
    const moveFocusToMsrp = useCallback(() => {
        if (sortIconColumn !== 'msrp') {
            setSortReversed(false);
        }
        setSortIconColumn('msrp');
    }, [sortIconColumn]);

    let header = strings.BuildData;
    const buildHeader = function (a, b) {
        return a + ' ' + b;
    };
    if ((props.connectedVehicle.VIN)) {
        header = buildHeader(header, ' - ');
        header = buildHeader(header, strings.VINShort);
        header = buildHeader(header, ':');
        header = buildHeader(header, props.connectedVehicle.VIN);
    }

    let options: ReactElement[] | null = null;
    if (props.connectedVehicle.option && props.connectedVehicle.option.length > 0) {
        const indexedOptions: OPTION[] = [];
        const vehOptions = [...props.connectedVehicle.option];
        let sortedOptions: OPTION[] = [];
        if ((sortIconColumn === 'none') || (sortIconColumn === 'desc')) {
            sortedOptions = vehOptions.sort((a, b) => (a.desc > b.desc ? 1 : -1));
            if (sortReversed) {
                sortedOptions = vehOptions.sort((a, b) => (b.desc > a.desc ? 1 : -1));
            }
        }
        if (sortIconColumn === 'code') {
            sortedOptions = vehOptions.sort((a, b) => (a.code > b.code ? 1 : -1));
            if (sortReversed) {
                sortedOptions = vehOptions.sort((a, b) => (b.code > a.code ? 1 : -1));
            }
        }
        if (sortIconColumn === 'msrp') {
            sortedOptions = vehOptions.sort((a, b) => (a.msrpSort > b.msrpSort ? 1 : -1));
            if (sortReversed) {
                sortedOptions = vehOptions.sort((a, b) => (b.msrpSort > a.msrpSort ? 1 : -1));
            }
        }
        sortedOptions.forEach((option, index) => {
            indexedOptions.push(
                {
                    index: index + 1,
                    code: option.code,
                    desc: option.desc,
                    msrp: option.msrp,
                    msrpSort: option.msrpSort,
                    type: option.type,
                },
            );
        });
        options = indexedOptions.map(options => <span className={options.index % 2 === 0 ? css.optionRow : css.optionRowOdd}
            id={options.code}
            key={random(99999)}>
            <div className={css.optionDescArea}>
                <div className={css.optionDesc}>{options.desc}</div>
            </div>
            <div className={css.optionCodeArea}>
                <div className={css.optionCode}>{options.code}</div>
            </div>
            <div className={css.optionMSRPArea}>
                <div className={css.optionMsrp}>{options.msrp}</div>
            </div>
        </span>);
    }

    let descSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'desc') {
        descSortIcon = <IconSortAscending id='descSortIcon' className={css.optionIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            descSortIcon = <IconSortDescending id='descSortIcon2' className={css.optionIcon} onClick={() => reverseSort()}/>;
        }
    }

    let codeSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'code') {
        codeSortIcon = <IconSortAscending id='codeSortIcon' className={css.optionIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            codeSortIcon = <IconSortDescending id='codeSortIcon2' className={css.optionIcon} onClick={() => reverseSort()}/>;
        }
    }

    let msrpSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'msrp') {
        msrpSortIcon = <IconSortAscending id='msrpSortIcon' className={css.optionIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            msrpSortIcon = <IconSortDescending id='msrpSortIcon2'className={css.optionIcon} onClick={() => reverseSort()}/>;
        }
    }

    let optionsArea: ReactElement | null = null;
    if (props.connectedVehicle.option && props.connectedVehicle.option.length > 0) {
        optionsArea = <div>
            <span className={css.optionHeader}>
                <div className={css.optionDescArea}>
                    <div id='optionDescLit' className={css.optionDescLit} onClick={moveFocusToDesc}>
                        {strings.OptionDesc}
                    </div>
                    {descSortIcon}
                </div>
                <div className={css.optionCodeArea}>
                    <div id='optionCodeLit' className={css.optionCodeLit} onClick={moveFocusToCode}>
                        {strings.OptionCode}
                    </div>
                    {codeSortIcon}
                </div>
                <div className={css.optionMSRPArea}>
                    <div id='optionMSRPLit' className={css.optionMSRPLit} onClick={moveFocusToMsrp}>
                        {strings.MSRP}
                    </div>
                    {msrpSortIcon}
                </div>
            </span>
            <ScrollBar scrollVertical>
                <div className={css.options}>
                    {options}
                </div>
            </ScrollBar>
        </div>;
    }

    return (
        <div>
            <span className={css.header}>
                <div className={css.headerLit}>{strings.ViewVehicleBuildData}</div>
                <div className={css.icon}>
                    <IconSeeMore onClick={() => toggleBuild()} />
                </div>
            </span>
            <Dialog
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={buildOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={header}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => toggleBuild()}
                onBackDropClick={() => toggleBuild()}
                width={'full'}
            >
                <div>
                    <span className={css.infoHeader}>
                        <div className={css.buildCodesLit}>
                            {strings.BuildCodes}
                        </div>
                        <IconPrint className={css.printIcon} onClick={() => printCV()} />
                    </span>
                    {optionsArea}
                </div>
            </Dialog>
        </div>
    );
};

export default BuildData;